// src/components/TopBar.js
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { FaUserCircle, FaChevronDown, FaSignOutAlt, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import axios from "axios";
import NotificationsBell from "./NotificationsBell";
import CruzRojaLogo from "../assets/logos/cruz-roja-logo.png";

const BACKEND_URL = "https://vol-backend.onrender.com";

function firstNonEmpty(obj, keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

export default function TopBar({ uid, userInfo, userRole }) {
  const navigate  = useNavigate();
  const menuRef   = useRef(null);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [photoUrl, setPhotoUrl]   = useState(null);
  const [fullName, setFullName]   = useState("");
  const [subLabel, setSubLabel]   = useState("");

  // ── Cargar foto + nombre + coordinación ──────────────────────────
  useEffect(() => {
    if (!uid) return;

    const load = async () => {
      try {
        const user  = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();

        // Foto
        try {
          const r = await axios.get(`${BACKEND_URL}/documentos/mios`, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: () => true,
          });
          if (r.status === 200 && r.data) {
            const d = r.data;
            const url = d.foto_url || d.fotoUrl || d.foto || d.fotoFirebaseUrl || null;
            setPhotoUrl(typeof url === "string" ? url : null);
          }
        } catch { /* sin foto */ }

        // Nombre + coordinación desde userInfo (ya viene de App.js)
        if (userInfo) {
          const nombre = firstNonEmpty(userInfo, ["nombre", "name"]) || "";
          const apPat  = firstNonEmpty(userInfo, ["apellido_pat","apellidoPat","apellido_paterno"]) || "";
          const apMat  = firstNonEmpty(userInfo, ["apellido_mat","apellidoMat","apellido_materno"]) || "";
          const full   = [nombre, apPat, apMat].filter(Boolean).join(" ");
          setFullName(full || user.email || "");

          // Sub-etiqueta: coordinación o rol
          const coord = firstNonEmpty(userInfo, ["coordinacion","coordinacion2","coordinacion_nombre"]);
          if (coord && coord !== "__ANY__") {
            setSubLabel(coord);
          } else {
            const rol = (userInfo?.rol?.nombre_rol || userRole || "").toUpperCase();
            setSubLabel(rol === "ADMIN" ? "Administración Nacional" : rol === "MODERADOR" ? "Coordinación Estatal" : "");
          }
        }
      } catch { /* silencioso */ }
    };

    load();
  }, [uid, userInfo, userRole]);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try { await signOut(auth); } catch { /* */ }
    navigate("/Ingresa", { replace: true });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0, left: 0, right: 0,
        height: "60px",
        backgroundColor: "#cc0000",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      {/* ── LOGO ── */}
      <Box
        sx={{
          display: "flex", alignItems: "center",
          pl: "260px", // mismo ancho que el sidebar
        }}
      >
        {/* Espacio vacío — el logo real está en el sidebar */}
      </Box>

      {/* ── DERECHA: campana + perfil ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>

        {/* Campana */}
        <NotificationsBell uid={uid} />

        {/* Divider */}
        <Box sx={{ width: "1px", height: 28, backgroundColor: "rgba(255,255,255,0.3)" }} />

        {/* Avatar + nombre dropdown */}
        <Box
          ref={menuRef}
          sx={{ position: "relative" }}
        >
          <Box
            onClick={() => setMenuOpen(o => !o)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              cursor: "pointer",
              px: 1, py: 0.5, borderRadius: 2,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.15)" },
              transition: "0.15s",
            }}
          >
            {/* Avatar */}
            {photoUrl ? (
              <Box sx={{
                width: 34, height: 34, borderRadius: "50%",
                overflow: "hidden", border: "2px solid rgba(255,255,255,0.5)",
                flexShrink: 0,
              }}>
                <img src={photoUrl} alt="avatar"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  referrerPolicy="no-referrer"
                />
              </Box>
            ) : (
              <FaUserCircle size={34} color="rgba(255,255,255,0.85)" />
            )}

            {/* Nombre + rol */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Typography sx={{
                color: "#fff", fontSize: "0.78rem",
                fontWeight: 700, lineHeight: 1.2,
                textTransform: "uppercase", maxWidth: 180,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {fullName || "Cargando..."}
              </Typography>
              {subLabel && (
                <Typography sx={{
                  color: "rgba(255,255,255,0.75)", fontSize: "0.68rem",
                  lineHeight: 1.2, maxWidth: 180,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {subLabel}
                </Typography>
              )}
            </Box>

            <FaChevronDown
              size={10} color="rgba(255,255,255,0.7)"
              style={{ transition: "0.2s", transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </Box>

          {/* Dropdown menú */}
          {menuOpen && (
            <Box sx={{
              position: "absolute", top: 46, right: 0,
              backgroundColor: "#fff", borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              minWidth: 180, zIndex: 99999, overflow: "hidden",
            }}>
              <Box
                onClick={() => { setMenuOpen(false); navigate("/MiPerfil"); }}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.2,
                  px: 2, py: 1.4, cursor: "pointer",
                  "&:hover": { backgroundColor: "#fef2f2" },
                  transition: "0.15s",
                }}
              >
                <FaUser size={13} color="#ff3333" />
                <Typography sx={{ fontSize: "0.85rem", color: "#333" }}>Mi perfil</Typography>
              </Box>

              <Box sx={{ height: "1px", backgroundColor: "#f0f0f0" }} />

              <Box
                onClick={handleLogout}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.2,
                  px: 2, py: 1.4, cursor: "pointer",
                  "&:hover": { backgroundColor: "#fef2f2" },
                  transition: "0.15s",
                }}
              >
                <FaSignOutAlt size={13} color="#ff3333" />
                <Typography sx={{ fontSize: "0.85rem", color: "#333" }}>Cerrar sesión</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}